import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { observer } from "mobx-react";
import { AppLayout } from "../shared/layout";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Users,
  FileText,
  AlertCircle,
  CheckCircle,
  DollarSign,
  Activity,
  Upload,
  PieChart,
  Globe,
  BarChart3,
  Receipt,
  TrendingUp,
} from "lucide-react";
import {
  getDashboardStats,
  getDetailedReport,
  getPaymentCountries,
  getPaymentsByCountry,
  getCountryPaymentFinancials,
} from "../../../data/requests/dashboard-requests";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "@/components/ui/use-toast";
import { countryOptions } from "../../../utils/country-options";

const StatsCard = ({ title, value, description, icon: Icon, color }) => {
  const colorClasses = {
    blue: "text-blue-600 bg-blue-50",
    green: "text-green-600 bg-green-50",
    yellow: "text-yellow-600 bg-yellow-50",
    red: "text-red-600 bg-red-50",
    purple: "text-purple-600 bg-purple-50",
    indigo: "text-indigo-600 bg-indigo-50",
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        {Icon && (
          <div
            className={`p-2 rounded-full ${
              colorClasses[color] || colorClasses.blue
            }`}
          >
            <Icon className="h-4 w-4" />
          </div>
        )}
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{value}</div>
        {description && (
          <p className="text-xs text-muted-foreground mt-1">{description}</p>
        )}
      </CardContent>
    </Card>
  );
};

export const ReportsScreen = observer(() => {
  const [activeTab, setActiveTab] = useState("overview");
  const [selectedCountry, setSelectedCountry] = useState("");
  const [selectedPayments, setSelectedPayments] = useState([]);
  const [financialTotals, setFinancialTotals] = useState(null);

  // Fetch dashboard statistics
  const { data: statsData, isLoading: statsLoading } = useQuery({
    queryKey: ["dashboard-stats"],
    queryFn: () => getDashboardStats().then((res) => res.data),
    staleTime: 2 * 60 * 1000, // 2 minutes cache
  });

  // Fetch detailed report
  const { data: detailedData, isLoading: detailedLoading } = useQuery({
    queryKey: ["detailed-report"],
    queryFn: () => getDetailedReport().then((res) => res.data),
    staleTime: 2 * 60 * 1000,
  });

  // Fetch payments by country
  const { data: paymentsByCountryData, isLoading: paymentsLoading } = useQuery({
    queryKey: ["payments-by-country", selectedCountry],
    queryFn: () => getPaymentsByCountry(selectedCountry).then((res) => res.data),
    enabled: !!selectedCountry,
    staleTime: 2 * 60 * 1000,
  });

  const stats = statsData?.statistics || {};

  const handlePaymentSelection = (paymentId, checked) => {
    if (checked) {
      setSelectedPayments((prev) => [...prev, paymentId]);
    } else {
      setSelectedPayments((prev) => prev.filter((id) => id !== paymentId));
    }
    setFinancialTotals(null); // Reset totals when selection changes
  };

  const handleSelectAllPayments = (checked) => {
    if (checked && paymentsByCountryData?.payments) {
      setSelectedPayments(paymentsByCountryData.payments.map((p) => p.id));
    } else {
      setSelectedPayments([]);
    }
    setFinancialTotals(null); // Reset totals when selection changes
  };

  const handleCalculateFinancials = async () => {
    if (selectedPayments.length === 0) {
      toast({
        title: "Hata",
        description: "Lütfen en az bir payment seçin.",
        variant: "destructive",
      });
      return;
    }

    try {
      const response = await getCountryPaymentFinancials(selectedCountry, selectedPayments);
      setFinancialTotals(response.data.financialTotals);
      toast({
        title: "Başarılı",
        description: "Finansal toplamlar hesaplandı.",
      });
    } catch (error) {
      toast({
        title: "Hata",
        description: "Finansal toplamlar hesaplanamadı.",
        variant: "destructive",
      });
    }
  };

  const financialData = [
    {
      label: "Total Commission Amount",
      value: financialTotals?.totalCommissionAmount || 0,
      icon: DollarSign,
      color: "blue",
    },
    {
      label: "Total Paid Amount",
      value: financialTotals?.totalPaidAmount || 0,
      icon: CheckCircle,
      color: "green",
    },
    {
      label: "Total Luca Amount",
      value: financialTotals?.totalLucaAmount || 0,
      icon: Receipt,
      color: "orange",
    },
    {
      label: "Total Luca Ready Amount",
      value: financialTotals?.totalLucaReadyAmount || 0,
      icon: TrendingUp,
      color: "emerald",
    },
    {
      label: "Total In Bank Amount",
      value: financialTotals?.totalInBankAmount || 0,
      icon: DollarSign,
      color: "cyan",
    },
    {
      label: "Total Accounting Amount",
      value: financialTotals?.totalAccountingAmount || 0,
      icon: FileText,
      color: "purple",
    },
    {
      label: "Total Invoice Uploaded Amount",
      value: financialTotals?.totalInvoiceUploadedAmount || 0,
      icon: Upload,
      color: "indigo",
    },
    {
      label: "Total Auto Created Invoice Amount",
      value: financialTotals?.totalAutoCreatedInvoiceAmount || 0,
      icon: Activity,
      color: "teal",
    },
    {
      label: "Total Error Invoice Amount",
      value: financialTotals?.totalErrorInvoiceAmount || 0,
      icon: AlertCircle,
      color: "red",
    },
  ];

  const colorClasses = {
    blue: "bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 border-blue-300 dark:border-blue-700",
    green: "bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300 border-green-300 dark:border-green-700",
    purple: "bg-purple-100 dark:bg-purple-900/50 text-purple-700 dark:text-purple-300 border-purple-300 dark:border-purple-700",
    emerald: "bg-emerald-100 dark:bg-emerald-900/50 text-emerald-700 dark:text-emerald-300 border-emerald-300 dark:border-emerald-700",
    cyan: "bg-cyan-100 dark:bg-cyan-900/50 text-cyan-700 dark:text-cyan-300 border-cyan-300 dark:border-cyan-700",
    orange: "bg-orange-100 dark:bg-orange-900/50 text-orange-700 dark:text-orange-300 border-orange-300 dark:border-orange-700",
    red: "bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-300 border-red-300 dark:border-red-700",
    indigo: "bg-indigo-100 dark:bg-indigo-900/50 text-indigo-700 dark:text-indigo-300 border-indigo-300 dark:border-indigo-700",
    teal: "bg-teal-100 dark:bg-teal-900/50 text-teal-700 dark:text-teal-300 border-teal-300 dark:border-teal-700",
  };

  if (statsLoading) {
    return (
      <AppLayout title="Raporlar">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <Activity className="h-8 w-8 animate-spin mx-auto mb-2" />
            <p>Veriler yükleniyor...</p>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout title="Raporlar">
      <div className="space-y-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="overview">Genel Bakış</TabsTrigger>
            <TabsTrigger value="detailed">Detaylı Rapor</TabsTrigger>
            <TabsTrigger value="breakdown">Dağılımlar</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-6">
            {/* Core Metrics */}
            <div>
              <h2 className="text-xl font-semibold mb-4">Temel Metrikler</h2>
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                <StatsCard
                  title="Toplam Müşteri"
                  value={stats.totalClients || 0}
                  description="Sizin yönettiğiniz toplam müşteri sayısı"
                  icon={Users}
                  color="blue"
                />
                <StatsCard
                  title="Banka Bilgisi Eksik"
                  value={stats.clientsWithoutBankInfo || 0}
                  description="IBAN veya SWIFT bilgisi olmayan müşteriler"
                  icon={AlertCircle}
                  color="red"
                />
                <StatsCard
                  title="Fatura Yüklendi"
                  value={stats.commissionDetailsWithInvoice || 0}
                  description="Fatura süreci başlamış komisyonlar"
                  icon={Upload}
                  color="purple"
                />
              </div>
            </div>

            {/* Last Month Accounting */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <DollarSign className="h-5 w-5" />
                  Geçen Ay Accounting Ödemeleri
                </CardTitle>
                <CardDescription>
                  {statsData?.calculationPeriod &&
                    `${new Date(
                      statsData.calculationPeriod.lastMonthStart
                    ).toLocaleDateString("tr-TR")} - ${new Date(
                      statsData.calculationPeriod.lastMonthEnd
                    ).toLocaleDateString("tr-TR")}`}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {stats.lastMonthAccountingTotals &&
                Object.keys(stats.lastMonthAccountingTotals).length > 0 ? (
                  <div className="space-y-2">
                    {Object.entries(stats.lastMonthAccountingTotals).map(
                      ([currency, amount]) => (
                        <div
                          key={currency}
                          className="flex items-center justify-between p-3 bg-muted rounded-lg"
                        >
                          <span className="font-medium">{currency}</span>
                          <span className="text-xl font-bold">
                            {Number(amount).toLocaleString("tr-TR", {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}
                          </span>
                        </div>
                      )
                    )}
                  </div>
                ) : (
                  <p className="text-muted-foreground">
                    Geçen ay için Accounting statusünde ödeme bulunamadı.
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Additional Metrics */}
            <div>
              <h2 className="text-xl font-semibold mb-4">Ek İstatistikler</h2>
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                <StatsCard
                  title="Komisyonlu Müşteriler"
                  value={stats.clientsWithCommissions || 0}
                  description="En az bir komisyonu olan müşteriler"
                  icon={CheckCircle}
                  color="green"
                />
                <StatsCard
                  title="Toplam Komisyon"
                  value={stats.totalCommissions || 0}
                  description="Tüm komisyonların toplamı"
                  icon={FileText}
                  color="blue"
                />
                <StatsCard
                  title="Son 30 Gün"
                  value={stats.recentCommissionDetails || 0}
                  description="Son 30 günde oluşturulan komisyon detayları"
                  icon={Activity}
                  color="indigo"
                />
              </div>
            </div>
          </TabsContent>

          <TabsContent value="detailed" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Müşteri Detay Raporu</CardTitle>
                <CardDescription>
                  Tüm müşterileriniz ve komisyon durumları. "Mail Gönderildi"
                  sütunu mail gönderilmiş ama henüz fatura detayı olmayan
                  komisyonları gösterir.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {detailedLoading ? (
                  <div className="text-center py-8">
                    <Activity className="h-8 w-8 animate-spin mx-auto mb-2" />
                    <p>Detaylı rapor yükleniyor...</p>
                  </div>
                ) : detailedData?.clients && detailedData.clients.length > 0 ? (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Müşteri Adı</TableHead>
                          <TableHead>Email</TableHead>
                          <TableHead>Ülke</TableHead>
                          <TableHead className="text-center">
                            Banka Bilgisi
                          </TableHead>
                          <TableHead className="text-right">
                            Toplam Komisyon
                          </TableHead>
                          <TableHead
                            className="text-right"
                            title="Fatura süreci başlamış komisyonlar"
                          >
                            Fatura Yüklü
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {detailedData.clients.map((client) => (
                          <TableRow key={client.id}>
                            <TableCell className="font-medium">
                              {client.name}
                            </TableCell>
                            <TableCell>{client.email || "-"}</TableCell>
                            <TableCell>{client.country || "-"}</TableCell>
                            <TableCell className="text-center">
                              {client.hasBankInfo ? (
                                <Badge
                                  variant="success"
                                  className="bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-300"
                                >
                                  <CheckCircle className="h-3 w-3 mr-1" />
                                  Tam
                                </Badge>
                              ) : (
                                <Badge variant="destructive">
                                  <AlertCircle className="h-3 w-3 mr-1" />
                                  Eksik
                                </Badge>
                              )}
                            </TableCell>
                            <TableCell className="text-right">
                              {client.totalCommissions}
                            </TableCell>
                            <TableCell className="text-right">
                              {client.invoiceUploadedCount}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                ) : (
                  <p className="text-center text-muted-foreground py-8">
                    Müşteri bulunamadı.
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Country-based Payment Financial Feature */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Globe className="h-5 w-5" />
                  Ülke Bazlı Payment Finansal Raporu
                </CardTitle>
                <CardDescription>
                  Seçtiğiniz ülkeye ait payment'ları seçerek finansal toplamları görüntüleyin.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Country Selection */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="text-sm font-medium mb-2 block">Ülke Seçin</label>
                    <Select
                      value={selectedCountry}
                      onValueChange={(value) => {
                        setSelectedCountry(value);
                        setSelectedPayments([]);
                        setFinancialTotals(null);
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Ülke seçin..." />
                      </SelectTrigger>
                      <SelectContent>
                        {countryOptions.map((country) => (
                          <SelectItem key={country.value} value={country.value}>
                            {country.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-end">
                    <Button
                      onClick={handleCalculateFinancials}
                      disabled={selectedPayments.length === 0 || !selectedCountry}
                      className="w-full"
                    >
                      <Receipt className="h-4 w-4 mr-2" />
                      Finansal Toplamları Hesapla
                    </Button>
                  </div>
                </div>

                {/* Payments Selection */}
                {selectedCountry && paymentsByCountryData?.payments && (
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-sm font-medium">
                        {selectedCountry} Ülkesindeki Payment'lar ({paymentsByCountryData.payments.length})
                      </h3>
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="select-all"
                          checked={
                            paymentsByCountryData.payments.length > 0 &&
                            selectedPayments.length === paymentsByCountryData.payments.length
                          }
                          onCheckedChange={handleSelectAllPayments}
                        />
                        <label htmlFor="select-all" className="text-sm">
                          Tümünü Seç
                        </label>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {paymentsByCountryData.payments.map((payment) => (
                        <div
                          key={payment.id}
                          className="flex items-center space-x-3 p-3 border rounded-lg"
                        >
                          <Checkbox
                            id={`payment-${payment.id}`}
                            checked={selectedPayments.includes(payment.id)}
                            onCheckedChange={(checked) =>
                              handlePaymentSelection(payment.id, checked)
                            }
                          />
                          <div className="flex-1 min-w-0">
                            <label
                              htmlFor={`payment-${payment.id}`}
                              className="text-sm font-medium cursor-pointer truncate"
                              title={payment.name}
                            >
                              {payment.name}
                            </label>
                            <p className="text-xs text-muted-foreground">
                              {payment.invoiceId} • {payment.commissionCount} komisyon
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Financial Totals Display */}
                {financialTotals && (
                  <div>
                    <h3 className="text-sm font-medium mb-4 flex items-center gap-2">
                      <DollarSign className="h-4 w-4" />
                      Finansal Toplamlar
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4">
                      {financialData.map((item, index) => {
                        const Icon = item.icon;
                        return (
                          <Card key={index} className="border-l-4 border-l-primary">
                            <CardContent className="p-4">
                              <div className="flex items-center gap-3">
                                <div
                                  className={`h-10 w-10 rounded-full flex items-center justify-center flex-shrink-0 ${colorClasses[item.color]}`}
                                >
                                  <Icon className="h-5 w-5" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-xs text-muted-foreground truncate">
                                    {item.label}
                                  </p>
                                  <p className="text-xl font-bold mt-1">
                                    ${item.value?.toFixed(2) || "0.00"}
                                  </p>
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Loading State */}
                {selectedCountry && paymentsLoading && (
                  <div className="text-center py-8">
                    <Activity className="h-8 w-8 animate-spin mx-auto mb-2" />
                    <p>Payment'lar yükleniyor...</p>
                  </div>
                )}

                {/* Empty State */}
                {selectedCountry && !paymentsLoading && paymentsByCountryData?.payments?.length === 0 && (
                  <div className="text-center py-8 text-muted-foreground">
                    <Globe className="h-12 w-12 mx-auto mb-2 opacity-50" />
                    <p>Bu ülkede payment bulunamadı.</p>
                    {paymentsByCountryData?.availableCountries && paymentsByCountryData.availableCountries.length > 0 && (
                      <div className="mt-4 p-4 bg-muted rounded-lg">
                        <p className="text-sm font-medium mb-2">Mevcut ülkeler:</p>
                        <div className="flex flex-wrap gap-2 justify-center">
                          {paymentsByCountryData.availableCountries.map((country) => (
                            <Badge key={country} variant="secondary" className="text-xs">
                              {country}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="breakdown" className="space-y-6">
            {/* Commission Details by Status */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <PieChart className="h-5 w-5" />
                  Komisyon Detayları Duruma Göre
                </CardTitle>
                <CardDescription>
                  Statuslere göre dağılım (Invoice Uploaded, Accounting vb.)
                </CardDescription>
              </CardHeader>
              <CardContent>
                {stats.commissionDetailsByStatus &&
                stats.commissionDetailsByStatus.length > 0 ? (
                  <div className="space-y-2">
                    {stats.commissionDetailsByStatus.map((item, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-3 bg-muted rounded-lg"
                      >
                        <span className="font-medium">{item.status}</span>
                        <Badge>{item.count}</Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground">Veri bulunamadı.</p>
                )}
              </CardContent>
            </Card>

            {/* Clients by Country */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Globe className="h-5 w-5" />
                  Ülkelere Göre Müşteri Dağılımı
                </CardTitle>
              </CardHeader>
              <CardContent>
                {stats.clientsByCountry && stats.clientsByCountry.length > 0 ? (
                  <div className="space-y-2">
                    {stats.clientsByCountry.map((item, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-3 bg-muted rounded-lg"
                      >
                        <span className="font-medium">{item.country}</span>
                        <Badge variant="outline">{item.count} müşteri</Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground">Veri bulunamadı.</p>
                )}
              </CardContent>
            </Card>

            {/* Clients Needing Attention */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="h-5 w-5" />
                  Dikkat Gerektiren Müşteriler
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                    <span className="font-medium text-red-900">
                      Banka Bilgisi Eksik
                    </span>
                    <Badge variant="destructive">
                      {stats.clientsNeedingAttention?.withoutBankInfo || 0}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg">
                    <span className="font-medium text-yellow-900">
                      Komisyonu Olmayan
                    </span>
                    <Badge className="bg-yellow-600">
                      {stats.clientsNeedingAttention?.withoutCommissions || 0}
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </AppLayout>
  );
});
